import { Injectable, inject, signal } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { OnboardingStepperComponent } from '../components/onboarding-stepper/onboarding-stepper.component';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private modalCtrl = inject(ModalController);
  private authService = inject(AuthService);
  private toast = inject(ToastService);

  /** Whether the onboarding flow has ever been completed in this session. */
  private _completed = signal(false);
  readonly completed = this._completed.asReadonly();

  /**
   * Guard to prevent multiple concurrent onboarding modals.
   */
  private isPresenting = false;

  constructor() {
    // Initialise the signal with the stored completion flag
    try {
      if (localStorage.getItem('onboardingWalkthroughDone')) {
        this._completed.set(true);
      }
    } catch {}
  }

  /**
   * Starts the onboarding flow if the user is logged in,
   * hasn't already completed it, and no other instance is already running.
   */
  async startIfNeeded() {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    // Already showing the onboarding modal — ignore duplicate request
    if (this.isPresenting) {
      return;
    }

    try {
      if (localStorage.getItem('onboardingWalkthroughDone')) {
        this._completed.set(true);
        return;
      }
    } catch { return; }

    this.isPresenting = true;
    try {
      await this.presentOnboarding();
    } finally {
      this.isPresenting = false;
    }
  }

  async replay() {
    // Clear flag to allow replay
    try {
      localStorage.removeItem('onboardingWalkthroughDone');
      this._completed.set(false);
    } catch {}
    await this.presentOnboarding();
  }

  private async presentOnboarding() {
    const modal = await this.modalCtrl.create({
      component: OnboardingStepperComponent,
      cssClass: 'onboarding-overlay',
      backdropDismiss: false,
      showBackdrop: true,
      focusTrap: true,
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.completed) {
      try {
        localStorage.setItem('onboardingWalkthroughDone', '1');
      } catch {}
      this._completed.set(true);
    }
  }
}
